import type { Ingredient } from './types';
import type { IngredientInput } from './validation';
import { matchIngredient, type IngredientMatch } from './recipeProposal';
import { parseImporte, type ScannedLine } from './receipts';
import { parseUnit } from './importCatalog';
import { isPurchaseUnit, dimensionOf, toBase, type PurchaseUnit } from './units';

export interface CatalogPriceLine {
  index: number;
  description: string;
  name: string;
  match: IngredientMatch;
  action: 'update' | 'create' | 'choose' | 'ignore';
  unitPrice?: number;
  unit?: PurchaseUnit;
  warning?: string;
  comparison?: { previous: number; current: number; changePct: number; unit: PurchaseUnit };
}
export interface CatalogPricesProposal { lines: CatalogPriceLine[] }

/** El precio leído es por unidad facturada, no por el formato previo del catálogo. */
export function invoiceLinesToCatalogProposal(lines: ScannedLine[], catalog: Ingredient[]): CatalogPricesProposal {
  return { lines: lines.map((line, index) => {
    const description = line.description?.trim() ?? '';
    const name = description.replace(/\b\d+(?:[.,]\d+)?\s*(kg|g|gr|ml|cl|l|litros?|gramos?|kilos?)\b/gi, '').trim();
    const match = matchIngredient(name, catalog);
    // La unidad debe corresponder a la cantidad facturada: «botella 750 ml»
    // en la descripción no significa que el precio sea por mililitro.
    const unitToken = `${line.quantity ?? ''}`.match(/\b(kg|g|gr|ml|cl|l|litros?|gramos?|kilos?|uds?|pz|docena|lb|oz)\b/i)?.[1];
    const parsedUnit = unitToken ? parseUnit(unitToken) : undefined;
    const unit = isPurchaseUnit(parsedUnit) ? parsedUnit : undefined;
    const price = parseImporte(line.unitPrice);
    const unitPrice = price != null && Number.isFinite(price) && price > 0 ? price : undefined;
    // Un abono (cantidad o importe en negativo: envases retornables) no es un
    // precio de compra, y la limpieza no es un ingrediente.
    const abono = (parseImporte(line.quantity) ?? 0) < 0 || (parseImporte(line.total) ?? 0) < 0;
    const ignore = !name || abono || /\b(detergente|lavavajillas|lej[ií]a|envases?|papeler[ií]a|transporte|portes|descuento|iva|subtotal|total)\b/i.test(name);
    return { index, description, name, match,
      action: ignore ? 'ignore' : match.kind === 'exact' ? 'update' : 'choose',
      unitPrice, unit,
      comparison: match.kind === 'exact' && unit ? compareCatalogPrice(catalog.find((ing) => ing.ingredientId === match.candidates[0]?.ingredientId), unitPrice, 1, unit) : undefined,
      warning: !unit ? 'Indica la unidad y cantidad a las que corresponde el precio de esta factura.' : !unitPrice ? 'Falta un precio unitario válido sin IVA.' : undefined,
    };
  }) };
}

/** Comparación de compra sin merma ni IVA, sobre una misma unidad. */
export function compareCatalogPrice(current: Ingredient | undefined, price: number | undefined, qty: number, unit: PurchaseUnit) {
  if (!current || current.pricePending || !current.purchaseUnit || !(current.purchaseQty! > 0) || !(current.netPrice! > 0) || !(price! > 0) || !Number.isFinite(price) || !(qty > 0) || !Number.isFinite(qty) || dimensionOf(current.purchaseUnit) !== dimensionOf(unit)) return undefined;
  const previous = current.netPrice! / toBase(current.purchaseQty!, current.purchaseUnit) * toBase(1, unit);
  const next = price! / qty;
  return { previous, current: next, changePct: (next / previous - 1) * 100, unit };
}

/** Se llama solo tras revisar precio y formato, conservando el resto de la ficha. */
export function catalogPriceInput(current: Ingredient | undefined, name: string, price: number, qty: number, unit: PurchaseUnit): IngredientInput {
  return { ...current, name, group: current?.group ?? 'otros', vatPct: current?.vatPct ?? 0,
    purchaseQty: qty, purchaseUnit: unit, netPrice: price, pricePending: false,
    priceSource: 'factura', createdVia: current?.createdVia ?? 'factura', active: current?.active ?? true,
  };
}

/** Identity of an invoice product: a different supplier or pack is a separate ingredient. */
export function invoiceIngredientKey(input: Pick<Ingredient, 'name' | 'supplierId' | 'presentation' | 'purchaseQty' | 'purchaseUnit'>): string {
  const text = (value?: string) => (value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase().replace(/\s+/g, ' ');
  return JSON.stringify([text(input.name), input.supplierId ?? '', text(input.presentation), input.purchaseQty, input.purchaseUnit]);
}
